import { useRef, useState, useEffect, useCallback } from 'react';
import logger from '../logger';
import { loadScript } from '../utils';

const SCRIPT_ID = 'google-gsi-lib';
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

export default function useGoogleSignIn(
  clientId: string,
  lazyload: boolean,
  view: 'standard' | 'icon',
  onSuccess: (response) => void
) {
  const buttonRef = useRef<HTMLElement | null>(null);
  const doneRef = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [clickAfterLoad, setClickAfterLoad] = useState(false);
  const [canLoadSdk, setCanLoadSdk] = useState(lazyload ? false : true);

  useEffect(() => {
    if (!canLoadSdk) {
      return;
    }
    if (!loaded) {
      loadScript(SCRIPT_ID, SCRIPT_SRC, () => setLoaded(true));
      return;
    }
    if (
      !buttonRef.current ||
      typeof window.google?.accounts?.id === 'undefined' ||
      doneRef.current === true
    ) {
      return;
    }
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: response => {
        logger.info('Google Auth response', response);
        onSuccess(response);
      },
    });
    window.google.accounts.id.renderButton(buttonRef.current, {
      type: view,
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      logo_alignment: 'left',
      width: view === 'standard' ? buttonRef.current.clientWidth : undefined,
    });
    doneRef.current = true;

    if (clickAfterLoad) {
      // The Google-rendered button has replaced our own,
      // replay the click that brought us here
      const newButton = buttonRef.current.querySelector('svg');
      const ev = new Event('click', {
        bubbles: true,
        cancelable: false,
      });
      newButton.dispatchEvent(ev);
    }
  }, [loaded, clickAfterLoad, clientId, onSuccess, canLoadSdk, view]);

  const triggerLazyload = useCallback((triggerClick: boolean = false) => {
    setCanLoadSdk(true);
    setClickAfterLoad(!!triggerClick);
  }, []);

  return { buttonRef, triggerLazyload };
}
